import { useState } from 'react';
import { Box, Button, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const PRIMARY_SYMBOLS = [
  { label: '+', title: 'plus' },
  { label: '−', title: 'minus' },
  { label: '×', title: 'multiply' },
  { label: '÷', title: 'divide' },
  { label: '=', title: 'equals' },
  { label: '%', title: 'percent' },
  { label: '°', title: 'degrees' },
  { label: '²', title: 'squared' },
  { label: '³', title: 'cubed' },
  { label: '⁴', title: 'to the 4th' },
  { label: '√', title: 'square root' },
  { label: '∛', title: 'cube root' },
  { label: '^', title: 'power (caret)' },
  { label: '(', title: 'open bracket' },
  { label: ')', title: 'close bracket' },
  { label: '½', title: 'one half' },
  { label: '¼', title: 'one quarter' },
  { label: '¾', title: 'three quarters' },
  { label: '⅓', title: 'one third' },
  { label: '⅔', title: 'two thirds' },
  { label: 'π', title: 'pi' },
  { label: '±', title: 'plus or minus' },
  { label: '≠', title: 'not equal' },
  { label: '≈', title: 'approximately' },
  { label: 'α', title: 'alpha' },
  { label: '∴', title: 'therefore' },
  { label: '∞', title: 'infinity' },
  { label: '≤', title: 'less than or equal' },
  { label: '≥', title: 'greater than or equal' },
  { label: '<', title: 'less than' },
  { label: '>', title: 'greater than' },
  { label: '£', title: 'pound sterling' },
];

const SHAPE_SYMBOLS = [
  { label: '□', title: 'square / rectangle' },
  { label: '○', title: 'circle' },
  { label: '☆', title: 'star' },
  { label: '△', title: 'triangle' },
];

const MORE_SYMBOLS = [
  { label: '⁰', title: 'superscript 0' },
  { label: '¹', title: 'superscript 1' },
  { label: '⁵', title: 'to the 5th' },
  { label: '⁶', title: 'to the 6th' },
  { label: '⁷', title: 'to the 7th' },
  { label: '⁸', title: 'to the 8th' },
  { label: '⁹', title: 'to the 9th' },
  { label: 'ⁿ', title: 'to the nth' },
  { label: '⅕', title: 'one fifth' },
  { label: '⅖', title: 'two fifths' },
  { label: '⅗', title: 'three fifths' },
  { label: '⅘', title: 'four fifths' },
  { label: '⅛', title: 'one eighth' },
  { label: '⅜', title: 'three eighths' },
  { label: '⅝', title: 'five eighths' },
  { label: '⅞', title: 'seven eighths' },
  { label: '∠', title: 'angle' },
  { label: '△', title: 'triangle' },
  { label: '□', title: 'square / rectangle' },
  { label: '⊥', title: 'perpendicular' },
  { label: '∥', title: 'parallel' },
  { label: '∑', title: 'sum' },
  { label: '→', title: 'arrow / maps to' },
  { label: 'β', title: 'beta' },
  { label: 'θ', title: 'theta' },
  { label: '∝', title: 'proportional to' },
  { label: '∈', title: 'element of' },
  { label: '∉', title: 'not element of' },
  { label: '⊂', title: 'subset of' },
  { label: 'p', title: 'pence' },
];

const TEMPLATES = [
  { label: 'xⁿ', insert: 'xⁿ' },
  { label: '√( )', insert: '√( )' },
  { label: 'a/b', insert: '\\(\\frac{ }{ }\\)', title: 'fraction (LaTeX)' },
  { label: 'a b/c', insert: '\\( \\frac{ }{ }\\)', title: 'mixed number (LaTeX)' },
];

const UNITS = [
  { label: 'mm', insert: 'mm' },
  { label: 'cm', insert: 'cm' },
  { label: 'm', insert: 'm' },
  { label: 'km', insert: 'km' },
  { label: 'cm²', insert: 'cm²' },
  { label: 'cm³', insert: 'cm³' },
  { label: 'm²', insert: 'm²' },
  { label: 'km²', insert: 'km²' },
  { label: 'g', insert: 'g' },
  { label: 'kg', insert: 'kg' },
  { label: 'mg', insert: 'mg' },
  { label: 'ml', insert: 'ml' },
  { label: 'l', insert: 'l' },
  { label: 'km/h', insert: 'km/h' },
  { label: 'mph', insert: 'mph' },
  { label: 'm/s', insert: 'm/s' },
  { label: 'min', insert: 'min' },
  { label: 'hrs', insert: 'hrs' },
];

const btnSx = {
  minWidth: 34,
  height: 28,
  px: 0.5,
  fontSize: '0.82rem',
  fontFamily: 'monospace',
  border: '1px solid #a5b4fc',
  bgcolor: '#fff',
  color: '#3730a3',
  lineHeight: 1,
  textTransform: 'none',
  '&:hover': { bgcolor: '#ede9fe', borderColor: '#6366f1' },
};

const templateBtnSx = {
  ...btnSx,
  minWidth: 52,
  border: '1px solid #6ee7b7',
  color: '#065f46',
  '&:hover': { bgcolor: '#d1fae5', borderColor: '#10b981' },
};

const unitBtnSx = {
  ...btnSx,
  minWidth: 44,
  border: '1px solid #fbbf24',
  color: '#92400e',
  '&:hover': { bgcolor: '#fef3c7', borderColor: '#f59e0b' },
};

const MathToolbar = ({ getEditor, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [moreOpen, setMoreOpen] = useState(false);

  const insertChar = (char) => {
    const editor = getEditor?.();
    if (!editor) return;
    const range = editor.getSelection() ?? { index: Math.max(0, editor.getLength() - 1) };
    editor.insertText(range.index, char, 'user');
    editor.setSelection(range.index + char.length);
    editor.focus();
  };

  return (
    <Box sx={{ bgcolor: '#f0f4ff', borderRadius: 1, border: '1px solid #c7d2fe', mb: 0.5, overflow: 'hidden' }}>
      {/* Toggle header */}
      <Box
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 1, py: 0.5, cursor: 'pointer', userSelect: 'none',
          '&:hover': { bgcolor: 'rgba(99,102,241,0.07)' },
        }}
      >
        <Typography variant="caption" sx={{ color: '#4338ca', fontWeight: 700, fontSize: '0.72rem' }}>
          {open ? 'Hide' : 'Show'} Math Tools
        </Typography>
        {open ? <ExpandLessIcon sx={{ fontSize: 16, color: '#6366f1' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: '#6366f1' }} />}
      </Box>

      <Collapse in={open} unmountOnExit mountOnEnter>
        <Box sx={{ px: 1, pb: 1 }}>
          {/* Primary symbols row */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mb: 0.5 }}>
            {PRIMARY_SYMBOLS.map(({ label, title }) => (
              <Button
                key={label}
                size="small"
                title={title}
                onMouseDown={(e) => { e.preventDefault(); insertChar(label); }}
                sx={btnSx}
              >
                {label}
              </Button>
            ))}
          </Box>

          {/* More symbols toggle */}
          <Box
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setMoreOpen((v) => !v)}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, cursor: 'pointer', mb: 0.5 }}
          >
            <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 600, fontSize: '0.7rem', textDecoration: 'underline' }}>
              {moreOpen ? 'Fewer symbols' : 'More symbols'}
            </Typography>
            {moreOpen ? <ExpandLessIcon sx={{ fontSize: 13, color: '#6366f1' }} /> : <ExpandMoreIcon sx={{ fontSize: 13, color: '#6366f1' }} />}
          </Box>

          <Collapse in={moreOpen} unmountOnExit mountOnEnter>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mb: 0.75 }}>
              {MORE_SYMBOLS.map(({ label, title }) => (
                <Button
                  key={label}
                  size="small"
                  title={title}
                  onMouseDown={(e) => { e.preventDefault(); insertChar(label); }}
                  sx={btnSx}
                >
                  {label}
                </Button>
              ))}
            </Box>
          </Collapse>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.4 }}>
            <Typography variant="caption" sx={{ color: '#555', fontWeight: 600, fontSize: '0.7rem', minWidth: 70 }}>
              Shapes:
            </Typography>
            {SHAPE_SYMBOLS.map(({ label, title }) => (
              <Button
                key={label}
                size="small"
                title={title}
                onMouseDown={(e) => { e.preventDefault(); insertChar(label); }}
                sx={btnSx}
              >
                {label}
              </Button>
            ))}
          </Box>

          {/* Templates row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.4 }}>
            <Typography variant="caption" sx={{ color: '#555', fontWeight: 600, fontSize: '0.7rem', minWidth: 70 }}>
              Templates:
            </Typography>
            {TEMPLATES.map(({ label, insert }) => (
              <Button
                key={label}
                size="small"
                title={`Insert: ${insert}`}
                onMouseDown={(e) => { e.preventDefault(); insertChar(insert); }}
                sx={templateBtnSx}
              >
                {label}
              </Button>
            ))}
          </Box>

          {/* Units row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: '#555', fontWeight: 600, fontSize: '0.7rem', minWidth: 70 }}>
              Units:
            </Typography>
            {UNITS.map(({ label, insert }) => (
              <Button
                key={label}
                size="small"
                title={`Insert: ${insert}`}
                onMouseDown={(e) => { e.preventDefault(); insertChar(insert); }}
                sx={unitBtnSx}
              >
                {label}
              </Button>
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default MathToolbar;
